import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock, Film, User, RefreshCw, Bookmark, Eye, Heart, Plus, List, Tv, Layers, Users, Settings, MoreHorizontal, Share2, ShieldAlert, ShieldOff, Sparkles } from 'lucide-react';
import { supabase, getStoredSession } from '../lib/supabase';
import { queryKeys } from '../lib/queryKeys';
import { useAuth } from '../contexts/AuthContext';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { useFollowStatus } from '../hooks/useUserFollowsQuery';
import { useBlockStatus, useBlockUserMutation, useUnblockUserMutation } from '../hooks/useBlockUser';
import { EditProfileModal } from '../components/EditProfileModal';
import { CreateListModal } from '../components/CreateListModal';
import { AddMovieModal } from '../components/AddMovieModal';
import { OnboardingModal } from '../components/OnboardingModal';
import { BlockUserDialog } from '../components/BlockUserDialog';
import { FollowButton } from '../components/FollowButton';
import { ShareButton } from '../components/ShareButton';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { GENRES } from '../utils/constants';
import { useLists, type WatchlistMeta as ContextWatchlist } from '../contexts/ListsContext';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { effectiveTier } from '../utils/entitlements';

// Profile skeleton for loading state
function ProfileSkeleton() {
  return (
    <div className="min-h-screen bg-[#111111] px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button skeleton */}
        <Skeleton className="w-24 h-6 mb-8" />

        {/* Profile header skeleton */}
        <div className="flex items-start gap-6 mb-8">
          {/* Avatar skeleton */}
          <Skeleton className="w-24 h-24 md:w-32 md:h-32 rounded-full flex-shrink-0" />

          {/* Profile info skeleton */}
          <div className="flex-1 space-y-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>

        {/* Stats cards skeleton */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-[#1A1F26]/50 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4">
              <Skeleton className="h-10 w-10 md:h-12 md:w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-8" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>

        {/* Lists section skeleton */}
        <Skeleton className="h-6 w-24 mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-[#1A1F26]/50 rounded-xl p-4 space-y-3">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type CustomListFilter = 'all' | 'movie' | 'tv';

interface Watchlist {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  item_count: number;
  is_default: boolean;
  visibility: 'public' | 'private' | 'friends';
  media_filter?: 'movie' | 'tv' | 'mixed';
}

interface FollowedList {
  id: string;
  name: string;
  description: string | null;
  item_count: number;
  follower_count: number;
  media_filter: 'movie' | 'tv' | 'mixed';
  followed_at: string;
  owner: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

// Tier badge for profile headers — renders for Pro and Social tiers only
function TierBadge({ profile }: { profile: PublicProfile }) {
  const tier = effectiveTier({
    subscription_tier: profile.subscription_tier ?? undefined,
    is_pro: profile.is_pro ?? undefined,
    is_test_user: profile.is_test_user ?? undefined,
    test_pro_mode: profile.test_pro_mode as 'auto' | 'free' | 'social' | 'pro' | undefined,
  });
  if (tier === 'free') return null;
  const config = tier === 'pro'
    ? { label: 'Pro', bg: 'from-teal-500/15 to-teal-400/5', border: 'border-teal-500/25', text: 'text-teal-400' }
    : { label: 'Social', bg: 'from-purple-500/15 to-purple-400/5', border: 'border-purple-500/25', text: 'text-purple-400' };
  return (
    <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold tracking-wide rounded-full bg-gradient-to-r ${config.bg} border ${config.border} ${config.text} backdrop-blur-sm`}>
      {config.label}
    </span>
  );
}

// Identity-only fields from profiles_public (always visible)
interface ProfileIdentity {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  followers_count: number;
  following_count: number;
  visibility: 'public' | 'friends' | 'private'; // From profiles_public for locked copy
}

// Full profile (may be null if gated by visibility)
interface ProfileDetails {
  bio: string | null;
  location: string | null;
  is_public: boolean;
  visibility: 'public' | 'friends' | 'private';
  created_at: string;
  total_ratings: number;
  movies_watched_count: number;
  tv_watched_count: number;
  subscription_tier: string | null;
  is_pro: boolean | null;
  is_test_user: boolean | null;
  test_pro_mode: string | null;
}

// Combined profile for rendering
// Omit visibility from Partial<ProfileDetails> since it's already required in ProfileIdentity
type PublicProfile = ProfileIdentity & Partial<Omit<ProfileDetails, 'visibility'>> & { isLocked?: boolean };

interface TasteProfile {
  favorite_genre_ids: number[];
  favorite_movie_ids: number[];
  favorite_actor_ids: number[];
  favorite_director_ids: number[];
}

interface TastePickMovie {
  id: number;
  title: string;
  poster_path: string | null;
  release_date: string | null;
}

interface TastePickPerson {
  id: number;
  name: string;
  profile_path: string | null;
}

interface TastePickCardsData {
  movies: TastePickMovie[];
  actors: TastePickPerson[];
  directors: TastePickPerson[];
}

interface ProfileAboutData {
  short_bio: string | null;
  movie_personality_tags: string[];
  hard_pass_tags: string[];
  watch_context_tags: string[];
  weirdest_movie_experience: string | null;
  movie_hot_take: string | null;
  life_movie_title: string | null;
  life_movie_genre_id: number | null;
  all_time_favorites: TastePickMovie[];
  comfort_watches: TastePickMovie[];
}

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w185';
const MAX_TASTE_PICK_FETCH_PER_KIND = 12;
const MIN_ALL_TIME_FAVORITES = 3;
const MAX_ALL_TIME_FAVORITES = 5;
const PERSONALITY_TAG_OPTIONS = [
  'Hopeless romantic',
  'Thrill-seeker',
  'Sci-fi nerd',
  'Comfort-watcher',
  'A24 enjoyer',
  'Marvel stan',
  'Classic cinema lover',
  'Horror addict',
];
const HARD_PASS_TAG_OPTIONS = [
  'Jumpscare horror',
  'Slow burns',
  'Musicals',
  'Over 3h epics',
  'Animated',
  'Old movies (pre-2000)',
];
const WATCH_CONTEXT_TAG_OPTIONS = [
  'In theaters a lot',
  'At home on TV',
  'On laptop/headphones',
  'With friends/family',
  'Mostly solo',
];

function TastePickMovieCard({ movie }: { movie: TastePickMovie }) {
  const releaseYear = movie.release_date ? new Date(movie.release_date).getFullYear() : null;

  return (
    <div className="w-[164px] flex flex-col items-center rounded-xl ring-1 ring-white/[0.08] bg-[#1A1F26]/50 p-2">
      <div className="w-24 h-36 rounded-lg overflow-hidden bg-slate-700 mb-2">
        {movie.poster_path ? (
          <img
            src={`${TMDB_IMAGE_BASE}${movie.poster_path}`}
            alt={movie.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film size={28} className="text-slate-500" />
          </div>
        )}
      </div>
      <p className="text-sm font-medium text-white text-center line-clamp-2 min-h-[2.5rem]">
        {movie.title}
      </p>
      {releaseYear && <p className="text-xs text-slate-400 mt-1">{releaseYear}</p>}
    </div>
  );
}

function TastePickPersonCard({
  person,
  role,
}: {
  person: TastePickPerson;
  role?: 'Actor' | 'Director';
}) {
  return (
    <div className="w-[164px] flex flex-col items-center rounded-xl ring-1 ring-white/[0.08] bg-[#1A1F26]/50 p-2">
      <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-700 mb-2">
        {person.profile_path ? (
          <img
            src={`${TMDB_IMAGE_BASE}${person.profile_path}`}
            alt={person.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User size={28} className="text-slate-500" />
          </div>
        )}
      </div>
      <p className="text-sm font-medium text-white text-center line-clamp-2 min-h-[2.5rem]">
        {person.name}
      </p>
      {role && <p className="text-[11px] text-slate-400 mt-1">{role}</p>}
    </div>
  );
}

// Fetch function for profile data
// Uses split fetches: profiles_public for identity (always works), profiles for full data (may be gated)
async function fetchProfileByUsername(usernameLower: string): Promise<PublicProfile> {
  // Step 1: Get identity from profiles_public (always readable, includes visibility)
  const { data: identity, error: identityError } = await supabase
    .from('profiles_public')
    .select('id, username, display_name, avatar_url, followers_count, following_count, visibility')
    .eq('username_lower', usernameLower)
    .maybeSingle();

  if (identityError) {
    console.error('Error fetching profile identity:', identityError);
    throw new Error('Failed to load profile');
  }

  if (!identity) {
    // No row in profiles_public = user not found OR soft-deleted
    throw new Error('User not found');
  }

  // Step 2: Try to get full profile details (may be gated by visibility RLS)
  const { data: fullProfile, error: profileError } = await supabase
    .from('profiles')
    .select('bio, location, is_public, visibility, created_at, total_ratings, movies_watched_count, tv_watched_count, subscription_tier, is_pro, is_test_user, test_pro_mode')
    .eq('id', identity.id)
    .maybeSingle();

  // If we get an RLS error or no data, profile is locked (private/friends-only)
  if (profileError || !fullProfile) {
    // Return identity-only with locked flag
    // Use visibility from profiles_public (always available) for locked copy
    return {
      ...identity,
      visibility: identity.visibility || 'public',
      isLocked: true,
    };
  }

  // Full profile accessible
  return {
    ...identity,
    ...fullProfile,
    visibility: fullProfile.visibility || identity.visibility || 'public', // Prefer full profile visibility
    isLocked: false,
  };
}

export function ProfilePage() {
  const goBack = useBackNavigation();
  const { username } = useParams<{ username: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile: currentUserProfile, refreshProfile, session, completeOnboarding } = useAuth();
  const queryClient = useQueryClient();

  const usernameLower = username?.toLowerCase() || '';
  const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

  // React Query for profile data - route-keyed to prevent stale renders
  const {
    data: profile,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.profile.byUsername(usernameLower),
    queryFn: () => fetchProfileByUsername(usernameLower),
    enabled: !!username,
  });

  const error = queryError instanceof Error ? queryError.message : queryError ? 'Something went wrong' : null;

  const [showEditModal, setShowEditModal] = useState(false);
  const [showTastePicks, setShowTastePicks] = useState(false);
  const [showRepickModal, setShowRepickModal] = useState(false);

  // Check for ?edit=true query param (from Settings page)
  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (editParam === 'true' && user && profile && user.id === profile.id) {
      setShowEditModal(true);
      // Remove query param from URL
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('edit');
        return newParams;
      });
    }
  }, [searchParams, user, profile, setSearchParams]);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showAddMovieModal, setShowAddMovieModal] = useState(false);
  const [newlyCreatedList, setNewlyCreatedList] = useState<{ id: string; name: string; media_filter: 'movie' | 'tv' | 'mixed' } | null>(null);
  const [customListFilter, setCustomListFilter] = useState<CustomListFilter>('all');
  const [followedLists, setFollowedLists] = useState<FollowedList[]>([]);
  const [followedListsLoading, setFollowedListsLoading] = useState(true);
  // Phase 5F: Removed fetchWatchlists - lists use cache-first + SWR
  const { lists: contextWatchlists, createWatchlist, isHydrated: contextListsHydrated } = useLists();
  const {
    watchedCount: derivedWatchedCount,
    watchlistCount: derivedWatchlistCount,
    favoritesCount: derivedFavoritesCount,
    isHydrated,
  } = useUserLibrary();
  const isOwnProfile = !!user && currentUserProfile?.username?.toLowerCase() === username?.toLowerCase();

  const tasteProfileQuery = useQuery<TasteProfile | null>({
    queryKey: queryKeys.forYou.tasteProfile(user?.id || 'anon'),
    enabled: !!user && !!session?.access_token && isOwnProfile,
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No session');
      const response = await fetch(`${API_BASE_URL}/taste-profile`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load taste profile');
      }
      const data: { profile: TasteProfile | null } = await response.json();
      return data.profile;
    },
    staleTime: 10 * 60 * 1000,
  });

  const tasteProfile = isOwnProfile ? tasteProfileQuery.data ?? null : null;
  const favoriteGenreNames = tasteProfile?.favorite_genre_ids
    ? tasteProfile.favorite_genre_ids
        .map((id: number) => GENRES[id])
        .filter(Boolean)
        .slice(0, 3)
    : [];
  const tasteSeedStats =
    tasteProfile
      ? [
          { label: 'Genres', count: tasteProfile.favorite_genre_ids?.length ?? 0 },
          { label: 'Movies', count: tasteProfile.favorite_movie_ids?.length ?? 0 },
          { label: 'Actors', count: tasteProfile.favorite_actor_ids?.length ?? 0 },
          { label: 'Directors', count: tasteProfile.favorite_director_ids?.length ?? 0 },
        ].filter((item) => item.count > 0)
      : [];
  const hasTasteSeeds = tasteSeedStats.length > 0;
  const needsTasteDnaSetup = isOwnProfile && !hasTasteSeeds;
  const tasteMovieIdsForCards = [...new Set(tasteProfile?.favorite_movie_ids ?? [])].slice(0, MAX_TASTE_PICK_FETCH_PER_KIND);
  const tasteActorIdsForCards = [...new Set(tasteProfile?.favorite_actor_ids ?? [])].slice(0, MAX_TASTE_PICK_FETCH_PER_KIND);
  const tasteDirectorIdsForCards = [...new Set(tasteProfile?.favorite_director_ids ?? [])].slice(0, MAX_TASTE_PICK_FETCH_PER_KIND);

  const tastePickCardsQuery = useQuery<TastePickCardsData>({
    queryKey: [
      'profile',
      'taste-pick-cards',
      tasteMovieIdsForCards,
      tasteActorIdsForCards,
      tasteDirectorIdsForCards,
    ],
    enabled: !!tasteProfile && isOwnProfile && showTastePicks && !!session?.access_token,
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No session');
      const response = await fetch(`${API_BASE_URL}/user/taste-pick-cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          movieIds: tasteMovieIdsForCards,
          actorIds: tasteActorIdsForCards,
          directorIds: tasteDirectorIdsForCards,
        }),
      });
      if (!response.ok) throw new Error('Failed to load taste pick cards');
      return response.json();
    },
    staleTime: 10 * 60 * 1000,
  });

  const profileAboutQuery = useQuery<ProfileAboutData>({
    queryKey: ['profile-about', user?.id || 'anon'],
    enabled: !!user && !!session?.access_token && isOwnProfile,
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No session');
      const response = await fetch(`${API_BASE_URL}/user/profile-about`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load profile-about details');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const [showProfileAboutEditor, setShowProfileAboutEditor] = useState(false);
  const [shortBioInput, setShortBioInput] = useState('');
  const [personalityTagsInput, setPersonalityTagsInput] = useState<string[]>([]);
  const [hardPassTagsInput, setHardPassTagsInput] = useState<string[]>([]);
  const [watchContextTagsInput, setWatchContextTagsInput] = useState<string[]>([]);
  const [weirdestExperienceInput, setWeirdestExperienceInput] = useState('');
  const [hotTakeInput, setHotTakeInput] = useState('');
  const [lifeMovieTitleInput, setLifeMovieTitleInput] = useState('');
  const [lifeMovieGenreIdInput, setLifeMovieGenreIdInput] = useState<number | null>(null);
  const [allTimeFavoritesInput, setAllTimeFavoritesInput] = useState<TastePickMovie[]>([]);
  const [comfortWatchesInput, setComfortWatchesInput] = useState<TastePickMovie[]>([]);
  const [allTimeSearchQuery, setAllTimeSearchQuery] = useState('');
  const [comfortSearchQuery, setComfortSearchQuery] = useState('');
  const [allTimeSearchResults, setAllTimeSearchResults] = useState<TastePickMovie[]>([]);
  const [comfortSearchResults, setComfortSearchResults] = useState<TastePickMovie[]>([]);
  const [savingProfileAbout, setSavingProfileAbout] = useState(false);
  const hydratedCurrentEditorOpenRef = useRef(false);

  const genreOptions = useMemo(
    () =>
      Object.entries(GENRES)
        .map(([id, name]) => ({ id: Number(id), name }))
        .filter((item) => Number.isFinite(item.id) && !!item.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const hydrateProfileAboutEditor = useCallback((data: ProfileAboutData) => {
    setShortBioInput(data.short_bio ?? '');
    setPersonalityTagsInput(data.movie_personality_tags ?? []);
    setHardPassTagsInput(data.hard_pass_tags ?? []);
    setWatchContextTagsInput(data.watch_context_tags ?? []);
    setWeirdestExperienceInput(data.weirdest_movie_experience ?? '');
    setHotTakeInput(data.movie_hot_take ?? '');
    setLifeMovieTitleInput(data.life_movie_title ?? '');
    setLifeMovieGenreIdInput(data.life_movie_genre_id ?? null);
    setAllTimeFavoritesInput(data.all_time_favorites ?? []);
    setComfortWatchesInput(data.comfort_watches ?? []);
  }, []);

  useEffect(() => {
    if (!showProfileAboutEditor) {
      hydratedCurrentEditorOpenRef.current = false;
      return;
    }
    if (!profileAboutQuery.data || hydratedCurrentEditorOpenRef.current) {
      return;
    }
    hydrateProfileAboutEditor(profileAboutQuery.data);
    hydratedCurrentEditorOpenRef.current = true;
  }, [showProfileAboutEditor, profileAboutQuery.data, hydrateProfileAboutEditor]);

  const searchMoviesForProfileAbout = useCallback(async (query: string, signal?: AbortSignal): Promise<TastePickMovie[]> => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const response = await fetch(
      `${API_BASE_URL}/search/movie?query=${encodeURIComponent(trimmed)}&page=1&limit=8`,
      signal ? { signal } : undefined
    );
    if (!response.ok) return [];
    const data: { results?: Array<{ id: number; title: string; poster_path: string | null; release_date: string | null }> } = await response.json();
    return (data.results || []).map((movie) => ({
      id: movie.id,
      title: movie.title,
      poster_path: movie.poster_path ?? null,
      release_date: movie.release_date ?? null,
    }));
  }, []);

  useEffect(() => {
    const query = allTimeSearchQuery.trim();
    if (!showProfileAboutEditor || !query) {
      setAllTimeSearchResults([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const results = await searchMoviesForProfileAbout(query, controller.signal);
        const selectedIds = new Set(allTimeFavoritesInput.map((movie) => movie.id));
        setAllTimeSearchResults(results.filter((movie) => !selectedIds.has(movie.id)));
      } catch {
        // Aborted or network error — ignore
      }
    }, 250);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [showProfileAboutEditor, allTimeSearchQuery, allTimeFavoritesInput, searchMoviesForProfileAbout]);

  useEffect(() => {
    const query = comfortSearchQuery.trim();
    if (!showProfileAboutEditor || !query) {
      setComfortSearchResults([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const results = await searchMoviesForProfileAbout(query, controller.signal);
        const selectedIds = new Set(comfortWatchesInput.map((movie) => movie.id));
        setComfortSearchResults(results.filter((movie) => !selectedIds.has(movie.id)));
      } catch {
        // Aborted or network error — ignore
      }
    }, 250);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [showProfileAboutEditor, comfortSearchQuery, comfortWatchesInput, searchMoviesForProfileAbout]);

  const toggleTagInList = (tag: string, list: string[], setList: (next: string[]) => void) => {
    if (list.includes(tag)) {
      setList(list.filter((item) => item !== tag));
      return;
    }
    setList([...list, tag]);
  };

  const addMovieToList = (
    movie: TastePickMovie,
    list: TastePickMovie[],
    setList: (next: TastePickMovie[]) => void,
    max: number
  ) => {
    if (list.some((item) => item.id === movie.id) || list.length >= max) return;
    setList([...list, movie]);
  };

  const removeMovieFromList = (
    movieId: number,
    list: TastePickMovie[],
    setList: (next: TastePickMovie[]) => void
  ) => {
    setList(list.filter((movie) => movie.id !== movieId));
  };

  const handleSaveProfileAbout = async () => {
    if (!session?.access_token) {
      toast.error('You need to be logged in');
      return;
    }
    if (allTimeFavoritesInput.length < MIN_ALL_TIME_FAVORITES) {
      toast.error(`Add at least ${MIN_ALL_TIME_FAVORITES} all-time favorites`);
      return;
    }
    if (allTimeFavoritesInput.length > MAX_ALL_TIME_FAVORITES) {
      toast.error(`You can only add up to ${MAX_ALL_TIME_FAVORITES} all-time favorites`);
      return;
    }

    setSavingProfileAbout(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/profile-about`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          short_bio: shortBioInput.trim() || null,
          movie_personality_tags: personalityTagsInput,
          hard_pass_tags: hardPassTagsInput,
          watch_context_tags: watchContextTagsInput,
          weirdest_movie_experience: weirdestExperienceInput.trim() || null,
          movie_hot_take: hotTakeInput.trim() || null,
          life_movie_title: lifeMovieTitleInput.trim() || null,
          life_movie_genre_id: lifeMovieGenreIdInput ?? null,
          all_time_favorite_movie_ids: allTimeFavoritesInput.map((movie) => movie.id),
          comfort_watch_movie_ids: comfortWatchesInput.map((movie) => movie.id),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to save');
      }

      toast.success('Profile details saved');
      await profileAboutQuery.refetch();
      setShowProfileAboutEditor(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save profile details');
    } finally {
      setSavingProfileAbout(false);
    }
  };

  // Retry function - uses React Query's refetch
  const handleRetry = () => {
    refetch();
  };

  const handleRepickComplete = async () => {
    completeOnboarding();
    setShowRepickModal(false);
    await tasteProfileQuery.refetch();
    if (showTastePicks) {
      await tastePickCardsQuery.refetch();
    }
  };

  // Phase 5F: Removed forced fetchWatchlists() on mount
  // Lists use cache-first + SWR (refetchOnWindowFocus/Reconnect) + mutation invalidation
  // This prevents forced refetch on every profile view

  // Follow status via React Query (reactive to mutations)
  // Poll every 5s while profile is locked to detect when they follow back
  const followStatus = useFollowStatus(
    !isOwnProfile ? profile?.id ?? null : null,
    { refetchInterval: profile?.isLocked ? 5000 : undefined }
  );

  // Track previous is_mutual to detect unlock
  const prevIsMutualRef = useRef<boolean | undefined>(undefined);

  // Effect: refetch profile when is_mutual becomes true (friends-only unlock)
  useEffect(() => {
    const currentIsMutual = followStatus.data?.is_mutual;
    const prevIsMutual = prevIsMutualRef.current;

    // If is_mutual transitioned from false/undefined to true, refetch profile
    if (currentIsMutual === true && prevIsMutual !== true && profile?.isLocked) {
      refetch();
    }

    prevIsMutualRef.current = currentIsMutual;
  }, [followStatus.data?.is_mutual, profile?.isLocked, refetch]);

  // Derived: does target follow viewer?
  const targetFollowsViewer = followStatus.data?.follows_viewer ?? false;

  // Block status — fail-closed: loading/error treated as restricted, not 'none'
  const blockStatusQuery = useBlockStatus(!isOwnProfile ? profile?.id ?? null : null);
  const blockStatusLoading = blockStatusQuery.isLoading && blockStatusQuery.fetchStatus !== 'idle';
  const blockStatusError = blockStatusQuery.isError;
  const blockStatus = blockStatusQuery.data?.status ?? (blockStatusLoading || blockStatusError ? undefined : 'none');
  const isBlockedByViewer = blockStatus === 'viewer_blocked_target';
  const isBlockedByTarget = blockStatus === 'target_blocked_viewer' || blockStatusError;
  const isBlocked = isBlockedByViewer || isBlockedByTarget;

  // Block mutations
  const blockMutation = useBlockUserMutation();
  const unblockMutation = useUnblockUserMutation();
  const { toast } = useToast();

  // Three-dot menu state
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // Close menu on outside click
  useEffect(() => {
    if (!showMoreMenu) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        moreButtonRef.current && !moreButtonRef.current.contains(e.target as Node)
      ) {
        setShowMoreMenu(false);
      }
    }
    function handleScroll() {
      setShowMoreMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showMoreMenu]);

  // Position menu below button
  const openMoreMenu = useCallback(() => {
    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setShowMoreMenu((prev) => !prev);
  }, []);

  // Block/unblock handlers
  const handleBlock = () => {
    if (!profile) return;
    blockMutation.mutate(profile.id, {
      onSuccess: () => {
        toast.success(`Blocked @${profile.username}`);
        setShowBlockDialog(false);
        setShowMoreMenu(false);
      },
      onError: () => {
        toast.error('Failed to block user');
      },
    });
  };

  const handleUnblock = () => {
    if (!profile) return;
    unblockMutation.mutate(profile.id, {
      onSuccess: () => {
        toast.success(`Unblocked @${profile.username}`);
        setShowMoreMenu(false);
      },
      onError: () => {
        toast.error('Failed to unblock user');
      },
    });
  };

  // Share profile (extracted for menu use)
  const handleShareProfile = async () => {
    const shareUrl = `${window.location.origin}/u/${profile?.username}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied');
    } catch {
      // Fallback: try Web Share API
      if (navigator.share) {
        await navigator.share({ title: `@${profile?.username}'s profile`, url: shareUrl });
      }
    }
    setShowMoreMenu(false);
  };

  // Fetch other user's public lists via TanStack Query (cache-first, no loading flicker)
  const otherUserWatchlistsQuery = useQuery({
    queryKey: queryKeys.lists.publicLists(profile?.id ?? ''),
    enabled: !!profile?.id && !isOwnProfile,
    queryFn: async () => {
      // For other profiles, only fetch public lists (plus default lists which are always visible)
      const { data, error: fetchErr } = await supabase
        .from('watchlists')
        .select('id, name, slug, description, item_count, is_default, visibility, media_filter')
        .eq('user_id', profile!.id)
        .or('is_default.eq.true,visibility.eq.public')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });

      if (fetchErr) throw fetchErr;
      return (data ?? []) as Watchlist[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Combine watchlists: use context for own profile, TanStack Query data for others
  const watchlists: Watchlist[] = useMemo(() => {
    if (isOwnProfile) {
      return contextWatchlists.map((w: ContextWatchlist) => ({
        id: w.id,
        name: w.name,
        slug: w.slug,
        description: w.description ?? null,
        item_count: w.item_count,
        is_default: w.is_default,
        visibility: w.visibility,
        media_filter: w.media_filter,
      }));
    }
    return otherUserWatchlistsQuery.data ?? [];
  }, [isOwnProfile, contextWatchlists, otherUserWatchlistsQuery.data]);

  // Loading state: for own profile use context hydration, for others use TanStack loading (cache-first)
  const watchlistsLoading = isOwnProfile
    ? !contextListsHydrated
    : (otherUserWatchlistsQuery.isLoading && !otherUserWatchlistsQuery.data);

  // Counts ready: true when we have the data to display accurate counts
  const countsReady = isOwnProfile ? isHydrated : !watchlistsLoading;

  // Fetch followed lists (only for own profile)
  useEffect(() => {
    async function fetchFollowedLists() {
      if (!isOwnProfile || !user) {
        setFollowedListsLoading(false);
        return;
      }

      setFollowedListsLoading(true);

      try {
        const accessToken = getStoredSession()?.access_token;
        if (!accessToken) {
          setFollowedListsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/watchlists/followed`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setFollowedLists(data.watchlists || []);
        }
      } catch (err) {
        console.error('Error fetching followed lists:', err);
      } finally {
        setFollowedListsLoading(false);
      }
    }

    fetchFollowedLists();
  }, [isOwnProfile, user, API_BASE_URL]);

  const handleEditComplete = async () => {
    setShowEditModal(false);
    // Refresh the current user's profile in AuthContext
    await refreshProfile();
    // Also refresh the displayed profile via React Query
    refetch();
    // Cross-invalidate: settings cache should reflect any profile changes
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['settings', user.id] });
    }
  };

  // Handle list creation - context already adds to state, just open AddMovieModal
  const handleListCreated = (newList: { id: string; name: string; description?: string; visibility: string; media_filter: 'movie' | 'tv' | 'mixed' }) => {
    // Open AddMovieModal to add movies to the newly created list
    setNewlyCreatedList({ id: newList.id, name: newList.name, media_filter: newList.media_filter });
    setShowAddMovieModal(true);
  };

  // Handle closing AddMovieModal
  const handleAddMovieModalClose = () => {
    setShowAddMovieModal(false);
    setNewlyCreatedList(null);
  };

  // Get default list counts (use derived counts from UserLibraryContext when available for own profile)
  const watchlistCount = isOwnProfile && isHydrated
    ? derivedWatchlistCount
    : (watchlists.find(w => w.slug === 'watchlist')?.item_count || 0);
  const watchedCount = isOwnProfile && isHydrated
    ? derivedWatchedCount
    : (watchlists.find(w => w.slug === 'watched')?.item_count || 0);
  const favoritesCount = isOwnProfile && isHydrated
    ? derivedFavoritesCount
    : (watchlists.find(w => w.slug === 'favorites')?.item_count || 0);

  // Get list IDs for linking
  const watchlistId = watchlists.find(w => w.slug === 'watchlist')?.id;
  const watchedId = watchlists.find(w => w.slug === 'watched')?.id;
  const favoritesId = watchlists.find(w => w.slug === 'favorites')?.id;

  // Get custom lists (non-default) with filter applied
  // When filtering by movie/tv, also include 'mixed' lists since they accept both types
  const allCustomLists = watchlists.filter(w => !w.is_default && !['watchlist', 'watched', 'favorites'].includes(w.slug || ''));
  const customLists = customListFilter === 'all'
    ? allCustomLists
    : allCustomLists.filter(w => w.media_filter === customListFilter || w.media_filter === 'mixed');

  // Loading skeleton (render immediately, no full-page block)
  if (loading && !profile) {
    return <ProfileSkeleton />;
  }

  // Error state (only if no profile and not loading)
  if (!profile) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center gap-4">
        <div className="text-xl text-slate-400">{error || 'Profile not found'}</div>
        {error && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={18} />
            Try again
          </button>
        )}
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
        >
          <ArrowLeft size={20} />
          Back
        </button>
      </div>
    );
  }

  // Block status loading gate: show skeleton while block check is in-flight
  // Prevents flash of full profile before block status resolves
  if (blockStatusLoading && !isOwnProfile) {
    return <ProfileSkeleton />;
  }

  // Blocked by target (or block-status error — fail-closed): "This profile is not available"
  if (isBlockedByTarget && !isOwnProfile) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center gap-4">
        <div className="text-xl text-slate-400">This profile is not available.</div>
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
        >
          <ArrowLeft size={20} />
          Back
        </button>
      </div>
    );
  }

  // Viewer blocked target: Show blocked state with unblock option
  if (isBlockedByViewer && !isOwnProfile) {
    return (
      <div className="min-h-screen bg-[#111111] px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Profile Header - greyed out */}
          <div className="flex flex-col sm:flex-row items-start gap-6 mb-8">
            {/* Greyed avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full ring-4 ring-slate-500/20 overflow-hidden flex-shrink-0 opacity-50 grayscale">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center">
                  <User size={48} className="text-white" />
                </div>
              )}
            </div>

            {/* Profile info */}
            <div className="flex-1 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">{profile.display_name || profile.username}</h1>
                  <p className="text-slate-400">@{profile.username}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleUnblock}
                    disabled={unblockMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-transparent border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
                  >
                    <ShieldOff size={16} />
                    {unblockMutation.isPending ? 'Unblocking...' : 'Unblock'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Blocked message */}
          <div className="text-center py-12 bg-[#1A1F26]/30 rounded-xl">
            <ShieldAlert size={40} className="mx-auto text-slate-500 mb-3" />
            <p className="text-slate-400 text-lg">You blocked @{profile.username}</p>
            <p className="text-slate-500 text-sm mt-1">They can't see your profile or interact with you.</p>
          </div>
        </div>
      </div>
    );
  }

  // Locked profile (private or friends-only visibility, not own profile)
  // Show identity header with locked message - NOT a 404
  if (profile.isLocked && !isOwnProfile) {
    return (
      <div className="min-h-screen bg-[#111111] px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Profile Header - identity is always visible */}
          <div className="flex flex-col sm:flex-row items-start gap-6 mb-8">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full ring-4 ring-slate-500/20 overflow-hidden flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center">
                  <User size={48} className="text-white" />
                </div>
              )}
            </div>

            {/* Profile info */}
            <div className="flex-1 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">{profile.display_name || profile.username}</h1>
                  <p className="text-slate-400">@{profile.username}</p>
                </div>
                <div className="flex items-center gap-2">
                  <FollowButton userId={profile.id} />
                  <div className="relative">
                    <button
                      ref={moreButtonRef}
                      onClick={openMoreMenu}
                      className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      aria-label="More options"
                    >
                      <MoreHorizontal size={20} />
                    </button>
                    {showMoreMenu && createPortal(
                      <div
                        ref={menuRef}
                        className="fixed z-50 w-56 bg-[#1A1F26] border border-white/10 rounded-xl shadow-xl py-1 overflow-hidden"
                        style={{ top: menuPosition.top, right: menuPosition.right }}
                      >
                        <button
                          onClick={handleShareProfile}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 transition-colors"
                        >
                          <Share2 size={16} />
                          Share Profile
                        </button>
                        <div className="border-t border-white/[0.06] my-1" />
                        {isBlockedByViewer ? (
                          <button
                            onClick={handleUnblock}
                            disabled={unblockMutation.isPending}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50"
                          >
                            <ShieldOff size={16} />
                            {unblockMutation.isPending ? 'Unblocking...' : `Unblock @${profile.username}`}
                          </button>
                        ) : (
                          <button
                            onClick={() => { setShowBlockDialog(true); setShowMoreMenu(false); }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors"
                          >
                            <ShieldAlert size={16} />
                            Block @{profile.username}
                          </button>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                </div>
              </div>
              {/* Follower/following counts are always visible */}
              <p className="text-sm text-slate-400">
                <Link to={`/u/${profile.username}/followers`} className="hover:text-white transition-colors">
                  <span className="font-semibold text-white">
                    {profile.followers_count >= 1000 ? `${(profile.followers_count / 1000).toFixed(1)}K` : profile.followers_count}
                  </span> Followers
                </Link>
                <span className="mx-2">·</span>
                <Link to={`/u/${profile.username}/following`} className="hover:text-white transition-colors">
                  <span className="font-semibold text-white">{profile.following_count}</span> Following
                </Link>
              </p>
            </div>
          </div>

          {/* Locked message - different copy for friends vs private */}
          <div className="text-center py-12 bg-[#1A1F26]/30 rounded-xl">
            <Lock size={40} className="mx-auto text-slate-500 mb-3" />
            {profile.visibility === 'friends' ? (
              <>
                <p className="text-slate-400 text-lg">This profile is visible to friends only</p>
                <p className="text-slate-500 text-sm mt-1">
                  Follow them and if they follow you back, you'll become friends and see their full profile.
                </p>
              </>
            ) : (
              <>
                <p className="text-slate-400 text-lg">This profile is private</p>
                <p className="text-slate-500 text-sm mt-1">Only they can view their full profile.</p>
              </>
            )}
          </div>

          {/* Block User Dialog (locked profile branch) */}
          <BlockUserDialog
            isOpen={showBlockDialog}
            onClose={() => setShowBlockDialog(false)}
            onConfirm={handleBlock}
            isBlocking={blockMutation.isPending}
            username={profile.username}
          />
        </div>
      </div>
    );
  }

  // Public profile (or own profile)
  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Profile Content - no sticky header */}
      <div className="px-4 sm:px-6 py-8 max-w-6xl mx-auto">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Profile Header Section */}
        <div className="flex flex-col sm:flex-row items-start gap-6 mb-8">
          {/* Avatar - larger, ring only */}
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-full ring-4 ring-teal-500/20 overflow-hidden flex-shrink-0">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-teal-400 to-orange-500 flex items-center justify-center">
                <User size={48} className="text-white" />
              </div>
            )}
          </div>

          {/* Profile info - horizontal layout */}
          <div className="flex-1 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl md:text-3xl font-bold text-white">{profile.display_name || profile.username}</h1>
                  {/* Tier badge — only for full profiles (tier fields are gated) */}
                  {!profile.isLocked && <TierBadge profile={profile} />}
                  {/* "Follows you" badge - shown when target follows viewer */}
                  {!isOwnProfile && targetFollowsViewer && (
                    <span className="px-2 py-0.5 text-xs font-medium text-slate-400 bg-slate-700/50 rounded">
                      Follows you
                    </span>
                  )}
                </div>
                <p className="text-slate-400">@{profile.username}</p>
              </div>
              <div className="flex items-center gap-2">
                {isOwnProfile ? (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-transparent border border-white/20 rounded-lg hover:bg-white/5 transition-colors w-fit"
                  >
                    <Settings size={16} />
                    Edit Profile
                  </button>
                ) : (
                  <>
                    {!isBlocked && <FollowButton userId={profile.id} />}
                  </>
                )}
                {/* Three-dot menu for non-own profiles */}
                {!isOwnProfile && (
                  <div className="relative">
                    <button
                      ref={moreButtonRef}
                      onClick={openMoreMenu}
                      className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      aria-label="More options"
                    >
                      <MoreHorizontal size={20} />
                    </button>
                    {showMoreMenu && createPortal(
                      <div
                        ref={menuRef}
                        className="fixed z-50 w-56 bg-[#1A1F26] border border-white/10 rounded-xl shadow-xl py-1 overflow-hidden"
                        style={{ top: menuPosition.top, right: menuPosition.right }}
                      >
                        <button
                          onClick={handleShareProfile}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 transition-colors"
                        >
                          <Share2 size={16} />
                          Share Profile
                        </button>
                        <div className="border-t border-white/[0.06] my-1" />
                        {isBlockedByViewer ? (
                          <button
                            onClick={handleUnblock}
                            disabled={unblockMutation.isPending}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50"
                          >
                            <ShieldOff size={16} />
                            {unblockMutation.isPending ? 'Unblocking...' : `Unblock @${profile.username}`}
                          </button>
                        ) : (
                          <button
                            onClick={() => { setShowBlockDialog(true); setShowMoreMenu(false); }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors"
                          >
                            <ShieldAlert size={16} />
                            Block @{profile.username}
                          </button>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                )}
                {/* Share button for own profile */}
                {isOwnProfile && (
                  <ShareButton
                    path={`/u/${profile.username}`}
                    title={`@${profile.username}'s profile`}
                    image={profile.avatar_url || undefined}
                    variant="icon-only"
                  />
                )}
              </div>
            </div>
            <p className="text-sm text-slate-400">
              <Link to={`/u/${profile.username}/followers`} className="hover:text-white transition-colors">
                <span className="font-semibold text-white">
                  {profile.followers_count >= 1000 ? `${(profile.followers_count / 1000).toFixed(1)}K` : profile.followers_count}
                </span> Followers
              </Link>
              <span className="mx-2">·</span>
              <Link to={`/u/${profile.username}/following`} className="hover:text-white transition-colors">
                <span className="font-semibold text-white">{profile.following_count}</span> Following
              </Link>
            </p>
            {profile.bio && <p className="text-slate-300 text-sm sm:text-base">{profile.bio}</p>}
          </div>
        </div>

        {/* Taste Snapshot Card (own profile only) */}
        {isOwnProfile && (
          <section className="mb-8">
            <div className="relative rounded-2xl bg-gradient-to-r from-teal-500 via-purple-500 to-orange-500 p-[1px] shadow-[0_0_40px_rgba(45,212,191,0.35)]">
              <div className="relative overflow-hidden rounded-2xl bg-[#05070A] px-4 py-4 sm:px-6 sm:py-5">
                <div className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_0_0,#22d3ee_0,transparent_45%),radial-gradient(circle_at_100%_100%,#a855f7_0,transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-500/20">
                      <Sparkles className="h-4 w-4 text-teal-300" />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Taste snapshot</p>
                      <h2 className="text-sm sm:text-base font-semibold text-white">Your movie DNA</h2>
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-slate-400">
                    <p className="uppercase tracking-[0.16em]">Seeds set</p>
                    <p className="text-sm font-semibold text-teal-300">
                      {tasteSeedStats.reduce((sum, stat) => sum + stat.count, 0)}
                    </p>
                  </div>
                </div>

                <div className="relative mt-4 grid gap-4 sm:grid-cols-3">
                  {/* Favorite genres */}
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-400">Favorite genres</p>
                    {favoriteGenreNames.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {favoriteGenreNames.map((name: string) => (
                          <span
                            key={name}
                            className="rounded-full bg-teal-500/15 px-2 py-1 text-[11px] font-medium text-teal-200"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500">Add a few genres to teach Filmple your taste.</p>
                    )}
                  </div>

                  {/* Seed counts */}
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-400">Onboarding picks</p>
                    {hasTasteSeeds ? (
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        {tasteSeedStats.map((stat) => (
                          <div
                            key={stat.label}
                            className="inline-flex items-center gap-1 rounded-lg bg-white/5 px-2 py-1 text-slate-200"
                          >
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">{stat.label}</span>
                            <span className="text-xs font-semibold text-white">{stat.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500">No onboarding picks yet.</p>
                    )}
                  </div>

                  {/* Hint text */}
                  <div className="flex flex-col justify-between gap-2 text-[11px] text-slate-400">
                    {needsTasteDnaSetup ? (
                      <>
                        <p>Fill your Movie DNA to personalize recommendations and chat from the start.</p>
                        <button
                          type="button"
                          onClick={() => setShowRepickModal(true)}
                          className="self-start rounded-full bg-teal-500/20 px-3 py-1 text-[11px] font-medium text-teal-200 hover:bg-teal-500/30 transition-colors"
                        >
                          Fill your Movie DNA
                        </button>
                      </>
                    ) : (
                      <>
                        <p>Used to warm up your recommendations and Filmply chat, especially before you've rated much.</p>
                        <p className="text-slate-500">
                          You can refine this later by updating your favorites and ratings.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {tasteProfile && hasTasteSeeds && (
                  <div className="relative mt-3 flex items-center justify-between gap-2 border-t border-white/5 pt-3">
                    <div className="text-[11px] text-slate-500">
                      {showTastePicks
                        ? 'These are your onboarding picks.'
                        : 'Want to see the exact movies, actors, and directors you picked?'}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTastePicks((prev) => !prev)}
                      className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-medium text-teal-200 hover:bg-white/10 transition-colors"
                    >
                      {showTastePicks ? 'Hide your picks' : 'View your picks'}
                    </button>
                  </div>
                )}

                {tasteProfile && hasTasteSeeds && showTastePicks && (
                  <div className="relative mt-4 space-y-4">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowRepickModal(true)}
                        className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-medium text-orange-200 hover:bg-white/10 transition-colors"
                      >
                        Repick choices
                      </button>
                    </div>
                    {tastePickCardsQuery.isLoading ? (
                      <p className="text-[11px] text-slate-500">Loading your picks...</p>
                    ) : (
                      <>
                        {(tastePickCardsQuery.data?.movies.length ?? 0) > 0 && (
                          <div>
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Movies</p>
                            <div className="flex flex-wrap gap-3">
                              {tastePickCardsQuery.data?.movies.map((movie) => (
                                <TastePickMovieCard key={`movie-${movie.id}`} movie={movie} />
                              ))}
                            </div>
                          </div>
                        )}

                        {((tastePickCardsQuery.data?.actors.length ?? 0) + (tastePickCardsQuery.data?.directors.length ?? 0)) > 0 && (
                          <div>
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">People</p>
                            <div className="flex flex-wrap gap-3">
                              {tastePickCardsQuery.data?.actors.map((actor) => (
                                <TastePickPersonCard key={`actor-${actor.id}`} person={actor} role="Actor" />
                              ))}
                              {tastePickCardsQuery.data?.directors.map((director) => (
                                <TastePickPersonCard key={`director-${director.id}`} person={director} role="Director" />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {isOwnProfile && (
          <section className="mb-8">
            <div className="rounded-2xl bg-[#0F1419] ring-1 ring-white/[0.08] p-4 sm:p-5">
              <button
                type="button"
                onClick={() => setShowProfileAboutEditor((prev) => !prev)}
                className="w-full text-left"
              >
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">About your movie identity</p>
                <p className="mt-1 text-sm text-white font-semibold">
                  Fill in details for a better personalized movie experience and help Filmple understand you better.
                </p>
                <p className="mt-2 text-xs text-teal-300">{showProfileAboutEditor ? 'Hide details' : 'Expand details'}</p>
              </button>

              {showProfileAboutEditor && profileAboutQuery.isLoading && (
                <div className="mt-5 flex justify-center py-8">
                  <RefreshCw size={20} className="animate-spin text-teal-400" />
                </div>
              )}
              {showProfileAboutEditor && !profileAboutQuery.isLoading && (
                <div className="mt-5 space-y-5">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">
                      Describe yourself in one sentence
                    </label>
                    <input
                      type="text"
                      value={shortBioInput}
                      onChange={(e) => setShortBioInput(e.target.value)}
                      maxLength={220}
                      placeholder="A hopeful cynic who rewatches sci-fi when life gets weird."
                      className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    />
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">Movie personality tags</p>
                    <div className="flex flex-wrap gap-2">
                      {PERSONALITY_TAG_OPTIONS.map((tag) => {
                        const active = personalityTagsInput.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTagInList(tag, personalityTagsInput, setPersonalityTagsInput)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              active ? 'bg-teal-500/20 text-teal-300' : 'bg-white/5 text-slate-300 hover:bg-white/10'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">All-time favorites (3-5)</p>
                    <div className="mb-2 flex flex-wrap gap-2">
                      {allTimeFavoritesInput.map((movie) => (
                        <button
                          key={`all-time-${movie.id}`}
                          type="button"
                          onClick={() => removeMovieFromList(movie.id, allTimeFavoritesInput, setAllTimeFavoritesInput)}
                          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-teal-500/15 text-teal-200 text-xs"
                        >
                          {movie.title}
                          <span className="text-teal-300">×</span>
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={allTimeSearchQuery}
                      onChange={(e) => setAllTimeSearchQuery(e.target.value)}
                      placeholder="Search movies to add favorites..."
                      className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    />
                    {allTimeSearchResults.length > 0 && (
                      <div className="mt-2 rounded-lg border border-white/10 bg-[#111827] overflow-hidden">
                        {allTimeSearchResults.map((movie) => (
                          <button
                            key={`all-time-result-${movie.id}`}
                            type="button"
                            onClick={() => addMovieToList(movie, allTimeFavoritesInput, setAllTimeFavoritesInput, 5)}
                            className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5 transition-colors"
                          >
                            {movie.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">Comfort watches</p>
                    <div className="mb-2 flex flex-wrap gap-2">
                      {comfortWatchesInput.map((movie) => (
                        <button
                          key={`comfort-${movie.id}`}
                          type="button"
                          onClick={() => removeMovieFromList(movie.id, comfortWatchesInput, setComfortWatchesInput)}
                          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-200 text-xs"
                        >
                          {movie.title}
                          <span className="text-cyan-300">×</span>
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={comfortSearchQuery}
                      onChange={(e) => setComfortSearchQuery(e.target.value)}
                      placeholder="Search comfort movies..."
                      className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                    />
                    {comfortSearchResults.length > 0 && (
                      <div className="mt-2 rounded-lg border border-white/10 bg-[#111827] overflow-hidden">
                        {comfortSearchResults.map((movie) => (
                          <button
                            key={`comfort-result-${movie.id}`}
                            type="button"
                            onClick={() => addMovieToList(movie, comfortWatchesInput, setComfortWatchesInput, 5)}
                            className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5 transition-colors"
                          >
                            {movie.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">No-go list / hard passes</p>
                    <div className="flex flex-wrap gap-2">
                      {HARD_PASS_TAG_OPTIONS.map((tag) => {
                        const active = hardPassTagsInput.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTagInList(tag, hardPassTagsInput, setHardPassTagsInput)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              active ? 'bg-rose-500/20 text-rose-300' : 'bg-white/5 text-slate-300 hover:bg-white/10'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">How you usually watch</p>
                    <div className="flex flex-wrap gap-2">
                      {WATCH_CONTEXT_TAG_OPTIONS.map((tag) => {
                        const active = watchContextTagsInput.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTagInList(tag, watchContextTagsInput, setWatchContextTagsInput)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              active ? 'bg-indigo-500/20 text-indigo-300' : 'bg-white/5 text-slate-300 hover:bg-white/10'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">
                        Weirdest movie experience
                      </label>
                      <textarea
                        rows={4}
                        value={weirdestExperienceInput}
                        onChange={(e) => setWeirdestExperienceInput(e.target.value)}
                        maxLength={1000}
                        placeholder="Watched Interstellar on a delayed flight with no sound, still cried."
                        className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">
                        Movie hill you'd die on
                      </label>
                      <textarea
                        rows={4}
                        value={hotTakeInput}
                        onChange={(e) => setHotTakeInput(e.target.value)}
                        maxLength={500}
                        placeholder="Speed Racer is a masterpiece and I refuse to debate this."
                        className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">
                        If your life was a movie, what would it be called?
                      </label>
                      <input
                        type="text"
                        value={lifeMovieTitleInput}
                        onChange={(e) => setLifeMovieTitleInput(e.target.value)}
                        maxLength={120}
                        placeholder="Chaos in Three Acts"
                        className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400 mb-2">
                        Life movie genre
                      </label>
                      <select
                        value={lifeMovieGenreIdInput ?? ''}
                        onChange={(e) => setLifeMovieGenreIdInput(e.target.value ? Number(e.target.value) : null)}
                        className="w-full rounded-lg bg-[#1A1F26]/70 border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-400"
                      >
                        <option value="">Pick a genre</option>
                        {genreOptions.map((genre) => (
                          <option key={genre.id} value={genre.id}>
                            {genre.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={handleSaveProfileAbout}
                      disabled={savingProfileAbout}
                      className="rounded-lg bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-400 disabled:opacity-60"
                    >
                      {savingProfileAbout ? 'Saving...' : 'Save profile details'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Stats Cards - 3 column grid, clickable */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
          {/* Watchlist stat */}
          {watchlistId ? (
            <Link
              to={`/list/${watchlistId}`}
              className="bg-[#1A1F26]/50 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4 hover:bg-[#1A1F26]/70 transition-colors"
            >
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Bookmark className="h-5 w-5 md:h-6 md:w-6 text-teal-400" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? watchlistCount : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Watchlist</p>
              </div>
            </Link>
          ) : !isOwnProfile ? (
            /* Other user's Watchlist is private (not returned by RLS) */
            <div className="bg-[#1A1F26]/30 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4">
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-slate-500/10 flex items-center justify-center shrink-0">
                <Lock className="h-5 w-5 md:h-6 md:w-6 text-slate-500" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-slate-500">—</p>
                <p className="text-xs md:text-base text-slate-500">Private</p>
              </div>
            </div>
          ) : (
            <div className="bg-[#1A1F26]/30 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4">
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Bookmark className="h-5 w-5 md:h-6 md:w-6 text-teal-400/50" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? 0 : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Watchlist</p>
              </div>
            </div>
          )}

          {/* Watched stat */}
          {watchedId ? (
            <Link
              to={`/list/${watchedId}`}
              className="bg-[#1A1F26]/50 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4 hover:bg-[#1A1F26]/70 transition-colors"
            >
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Eye className="h-5 w-5 md:h-6 md:w-6 text-teal-400" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? watchedCount : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Watched</p>
              </div>
            </Link>
          ) : (
            <div className="bg-[#1A1F26]/30 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4">
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Eye className="h-5 w-5 md:h-6 md:w-6 text-teal-400/50" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? 0 : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Watched</p>
              </div>
            </div>
          )}

          {/* Favorites stat */}
          {favoritesId ? (
            <Link
              to={`/list/${favoritesId}`}
              className="bg-[#1A1F26]/50 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4 hover:bg-[#1A1F26]/70 transition-colors"
            >
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Heart className="h-5 w-5 md:h-6 md:w-6 text-teal-400" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? favoritesCount : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Favorites</p>
              </div>
            </Link>
          ) : (
            <div className="bg-[#1A1F26]/30 rounded-xl p-3 md:p-6 flex items-center gap-3 md:gap-4">
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-teal-500/10 flex items-center justify-center shrink-0">
                <Heart className="h-5 w-5 md:h-6 md:w-6 text-teal-400/50" />
              </div>
              <div>
                <p className="text-xl md:text-3xl font-bold text-white">{countsReady ? 0 : '—'}</p>
                <p className="text-xs md:text-base text-slate-400">Favorites</p>
              </div>
            </div>
          )}
        </div>

        {/* Lists Section */}
        {watchlistsLoading ? (
          <section className="space-y-4 mb-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg md:text-xl font-semibold text-white">{isOwnProfile ? 'Your Lists' : 'Lists'}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-[#1A1F26]/30 rounded-xl p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-700 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-700 rounded w-20 mb-2" />
                      <div className="h-3 bg-[#1A1F26] rounded w-14" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <>
            {/* Custom Lists Section */}
            {(allCustomLists.length > 0 || isOwnProfile) && (
              <section className="space-y-4 mb-8">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg md:text-xl font-semibold text-white">{isOwnProfile ? 'Your Lists' : 'Lists'}</h2>
                  <div className="flex items-center gap-3">
                    {isOwnProfile && (
                      <button
                        onClick={() => setShowCreateListModal(true)}
                        className="flex items-center gap-1.5 text-teal-400 hover:text-teal-300 text-xs md:text-sm transition-colors"
                      >
                        <Plus size={14} />
                        Create
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter toggles */}
                {allCustomLists.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center bg-[#1A1F26]/50 rounded-full p-1">
                      <button
                        onClick={() => setCustomListFilter('all')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                          customListFilter === 'all'
                            ? 'bg-teal-500/20 text-teal-400'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setCustomListFilter('movie')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                          customListFilter === 'movie'
                            ? 'bg-teal-500/20 text-teal-400'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <Film size={12} />
                        Movies
                      </button>
                      <button
                        onClick={() => setCustomListFilter('tv')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                          customListFilter === 'tv'
                            ? 'bg-teal-500/20 text-teal-400'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <Tv size={12} />
                        TV
                      </button>
                    </div>
                  </div>
                )}

                {/* Lists grid */}
                {customLists.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {customLists.map((list) => (
                      <Link
                        key={list.id}
                        to={`/list/${list.id}`}
                        className="bg-[#1A1F26]/30 rounded-xl p-4 hover:bg-[#1A1F26]/50 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-3">
                          {/* Count badge - teal gradient */}
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500/20 to-teal-500/5 flex items-center justify-center shrink-0">
                            <span className="text-sm font-semibold text-teal-400">{list.item_count}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white truncate text-sm md:text-base">{list.name}</p>
                              {/* Media type badges */}
                              {list.media_filter && list.media_filter !== 'mixed' && (
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                  list.media_filter === 'movie'
                                    ? 'bg-teal-500/20 text-teal-400'
                                    : 'bg-purple-500/20 text-purple-400'
                                }`}>
                                  {list.media_filter === 'movie' ? (
                                    <><Film size={10} /> Movies</>
                                  ) : (
                                    <><Tv size={10} /> TV</>
                                  )}
                                </span>
                              )}
                              {list.media_filter === 'mixed' && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-slate-500/20 text-slate-400">
                                  <Layers size={10} /> Both
                                </span>
                              )}
                            </div>
                            <p className="text-xs md:text-sm text-slate-400">{list.item_count} items</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : allCustomLists.length > 0 ? (
                  <div className="text-center py-6 text-slate-500 text-sm">
                    No {customListFilter === 'movie' ? 'movie' : 'TV'} lists
                  </div>
                ) : isOwnProfile ? (
                  <div className="p-6 bg-[#1A1F26]/30 rounded-xl text-center">
                    <List size={32} className="mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-400 mb-3">No custom lists yet</p>
                    <button
                      onClick={() => setShowCreateListModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-400 rounded-lg transition-colors"
                    >
                      <Plus size={16} />
                      Create Your First List
                    </button>
                  </div>
                ) : null}
              </section>
            )}

            {/* Followed Lists Section (own profile only) */}
            {isOwnProfile && !followedListsLoading && followedLists.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg md:text-xl font-semibold text-white">Followed Lists</h2>
                    <Users className="h-4 w-4 text-slate-400" />
                  </div>
                  <button className="text-slate-400 hover:text-white text-xs md:text-sm">Discover more</button>
                </div>

                {/* Same grid layout */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {followedLists.map((list) => (
                    <Link
                      key={list.id}
                      to={`/list/${list.id}`}
                      className="bg-[#1A1F26]/30 rounded-xl p-4 hover:bg-[#1A1F26]/50 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        {/* Owner avatar instead of count */}
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shrink-0 overflow-hidden">
                          {list.owner.avatar_url ? (
                            <img
                              src={list.owner.avatar_url}
                              alt={list.owner.username}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User size={20} className="text-white" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white truncate text-sm md:text-base">{list.name}</p>
                            {/* Media type badge */}
                            {list.media_filter && list.media_filter !== 'mixed' && (
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                list.media_filter === 'movie'
                                  ? 'bg-teal-500/20 text-teal-400'
                                  : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {list.media_filter === 'movie' ? (
                                  <><Film size={10} /> Movies</>
                                ) : (
                                  <><Tv size={10} /> TV</>
                                )}
                              </span>
                            )}
                          </div>
                          <p className="text-xs md:text-sm text-slate-400">
                            by @{list.owner.username} · {list.item_count} items
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Edit Profile Modal */}
      {isOwnProfile && (
        <EditProfileModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditComplete}
          profile={{
            id: profile.id,
            username: profile.username,
            display_name: profile.display_name,
            avatar_url: profile.avatar_url,
            bio: profile.bio ?? null,
            location: profile.location ?? null,
            is_public: profile.is_public ?? true,
            // Ensure visibility has a default for legacy profiles
            visibility: profile.visibility || (profile.is_public ? 'public' : 'private'),
          }}
        />
      )}

      {/* Create List Modal */}
      {isOwnProfile && (
        <CreateListModal
          isOpen={showCreateListModal}
          onClose={() => setShowCreateListModal(false)}
          onCreated={handleListCreated}
          createWatchlist={createWatchlist}
        />
      )}

      {/* Add Movie Modal (shown after creating a list) */}
      {newlyCreatedList && (
        <AddMovieModal
          isOpen={showAddMovieModal}
          onClose={handleAddMovieModalClose}
          watchlistId={newlyCreatedList.id}
          watchlistName={newlyCreatedList.name}
          mediaFilter={newlyCreatedList.media_filter}
          isDefaultList={false}
          listSlug={null}
        />
      )}

      {/* Repick Onboarding Modal */}
      {isOwnProfile && (
        <OnboardingModal
          isOpen={showRepickModal}
          onComplete={handleRepickComplete}
        />
      )}

      {/* Block User Dialog */}
      {profile && !isOwnProfile && (
        <BlockUserDialog
          isOpen={showBlockDialog}
          onClose={() => setShowBlockDialog(false)}
          onConfirm={handleBlock}
          isBlocking={blockMutation.isPending}
          username={profile.username}
        />
      )}
    </div>
  );
}
